import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { createAppRouter } from './router'
import { watchForNewDeploy } from './utils/freshness'
import { createApiClient, provideApiClient } from './api'
import { applyLocale, i18n } from './i18n'
import { useAuthStore } from './stores/auth'
import { useUiStore } from './stores/ui'

import './styles/tokens.css'
import './styles/base.css'
import './styles/utilities.css'

/**
 * Bootstrap.
 *
 * The order below matters and is the only place it does:
 *
 * 1. Pinia is installed first, so stores exist.
 * 2. The API client is created with hooks that read the auth store lazily — the client needs the
 *    store to refresh, and the store needs the client to make requests. Resolving each side
 *    lazily is what breaks the cycle without a service locator.
 * 3. The client is provided *and* registered before the router mounts, because the router's guard
 *    may resume a session on the very first navigation.
 * 4. Localisation is installed before anything renders, and the stored locale is stamped onto
 *    `<html lang>` before the first paint — a screen reader picks its voice from that attribute,
 *    so French text inside `lang="en"` is read aloud in an English accent.
 */

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(i18n)

const client = createApiClient({
  // Same origin and no prefix by default. The generated operation paths are the core's own and
  // already carry `/api/v1`; this is an *origin*, not a path prefix. Setting it to `/api/v1` — as
  // this file used to — sends every request to `/api/v1/api/v1/…`.
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  getAccessToken: () => useAuthStore().accessToken,
  refresh: () => useAuthStore().refresh(),
  onSessionExpired: () => useAuthStore().onSessionExpired(),
})

provideApiClient(app, client)

// The theme is applied before the first paint so a dark-mode user does not get a white flash.
useUiStore().applyTheme()

// Likewise the locale, so `<html lang>` is right for the very first render rather than after it.
applyLocale()

app.use(createAppRouter())
app.mount('#app')

// A tab holding a pre-deploy index.html makes no request and so can never be corrected by a
// response header. This is the only place that can notice, and it is why it exists.
watchForNewDeploy()
